import { useContext } from 'react';
import { UserPreferencesContext } from '../contexts/UserPreferencesContext';

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}

// convenience hooks for slices of UserPreferencesContext
export function useTheme() {
  const {
    color,
    useSystemColor,
    font,
    size,
    width,
    wpm,
    setColor,
    setUseSystemColor,
    setFont,
    setSize,
    setWidth,
    setWpm,
  } = useUserPreferences();
  return {
    color,
    useSystemColor,
    font,
    size,
    width,
    wpm,
    setColor,
    setUseSystemColor,
    setFont,
    setSize,
    setWidth,
    setWpm,
  };
}

export function useSidebar() {
  const {
    showReaderSidebar,
    readerSidebarMode,
    toggleReaderSidebar,
    setReaderSidebarMode,
    setShowReaderSidebar,
    closeReaderSidebar,
  } = useUserPreferences();
  return {
    showSidebar: showReaderSidebar,
    sidebarMode: readerSidebarMode,
    toggleSidebar: toggleReaderSidebar,
    setSidebarMode: setReaderSidebarMode,
    setShowSidebar: setShowReaderSidebar,
    closeSidebar: closeReaderSidebar,
  };
}

export function useMobileNavSidebar() {
  const {
    showMobileSidebarNav,
    setShowMobileSidebarNav,
    toggleMobileSidebarNav,
    closeMobileSidebarNav,
  } = useUserPreferences();
  return {
    showMobileNavSidebar: showMobileSidebarNav,
    setShowMobileNavSidebar: setShowMobileSidebarNav,
    toggleMobileNavSidebar: toggleMobileSidebarNav,
    closeMobileNavSidebar: closeMobileSidebarNav,
  };
}

export function useSidebarNav() {
  const { sidebarNavPreferences, setSidebarNavSectionOpen } = useUserPreferences();
  return {
    sidebarNavPreferences,
    setSidebarNavSectionOpen,
  };
}

export function useReaderLayoutMode() {
  const { readerLayoutMode, setReaderLayoutMode } = useUserPreferences();
  return {
    readerLayoutMode,
    setReaderLayoutMode,
  };
}

export function usePremiumNotice() {
  const { preferences, dismissPremiumNotice } = useUserPreferences();

  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  // Check if notice should be shown
  const shouldShowNotice = () => {
    const dismissedAt = preferences.premiumNotice?.dismissedAt;

    // If never dismissed, show it
    if (!dismissedAt) {
      return true;
    }

    // Check if 30 days have passed
    const now = Date.now();
    const daysSinceDismissal = now - dismissedAt;

    return daysSinceDismissal >= THIRTY_DAYS_MS;
  };

  return {
    shouldShowNotice: shouldShowNotice(),
    dismissNotice: dismissPremiumNotice,
  };
}
